/**
 * A chat attachment's signed url, re-minted when it has gone stale.
 *
 * The thread's urls are signed for minutes, and a thread left open over lunch draws broken pictures
 * from urls that were good when it was read. The element's own `error` is the signal — no clock is
 * kept — and `GET /media/{id}/url` answers a fresh one, once per failure, so a file that is truly
 * gone (60-day retention) does not loop.
 */

import { useState } from 'react';

import { getMediaIdUrl } from '~/shared/api/generated/endpoints/media/media';

export interface SignedUrl {
  readonly url: string | null;
  /** Hand to the element's `onError`. */
  readonly onError: () => void;
  /** The url could not be re-minted: the file is gone. */
  readonly gone: boolean;
}

export function useSignedUrl(
  mediaId: number | null | undefined,
  url: string | null | undefined,
  artifact: 'primary' | 'poster',
): SignedUrl {
  const [fresh, setFresh] = useState<{ for: string | null; url: string | null } | null>(null);
  const [gone, setGone] = useState(false);
  const current = fresh !== null && fresh.for === (url ?? null) ? fresh.url : (url ?? null);

  const onError = () => {
    if (gone || typeof mediaId !== 'number' || mediaId <= 0) {
      setGone(true);
      return;
    }
    if (fresh !== null && fresh.for === (url ?? null)) {
      setGone(true);
      return;
    }
    getMediaIdUrl(mediaId, { artifact })
      .then((answer) => {
        setFresh({ for: url ?? null, url: answer.url ?? null });
      })
      .catch(() => {
        setGone(true);
      });
  };

  return { url: gone ? null : current, onError, gone };
}
